import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import {
  KEYWORD_WEIGHT_LABELS,
  getKeywordWeightLabel,
} from '../../utils/keywordWeightLabels';

/** Shared colour coding for weight numbers: red for the top tiers, orange for 0.9. */
export const getKeywordWeightColor = (weight: number, theme: Theme): string =>
  weight > 0.9
    ? theme.palette.error.main
    : weight > 0.8
    ? theme.palette.warning.main
    : theme.palette.text.secondary;

interface WeightNumberProps {
  weight: number;
}

const WeightNumber: React.FC<WeightNumberProps> = ({ weight }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        color: getKeywordWeightColor(weight, theme),
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        minWidth: 34,
        flexShrink: 0,
      }}
    >
      {weight}
    </Box>
  );
};

interface KeywordWeightOptionProps {
  weight: number;
}

/**
 * Body of a weight dropdown row: number, short label and the one-line
 * explanation underneath. Render inside a `MenuItem`.
 */
export const KeywordWeightOption: React.FC<KeywordWeightOptionProps> = ({
  weight,
}) => {
  const label = getKeywordWeightLabel(weight);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <WeightNumber weight={weight} />
        <Typography
          component="span"
          sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}
        >
          {label?.short ?? 'Custom weight'}
        </Typography>
      </Box>
      {label && (
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            lineHeight: 1.35,
            color: 'text.secondary',
            whiteSpace: 'normal',
            fontWeight: 400,
          }}
        >
          {label.description}
        </Typography>
      )}
    </Box>
  );
};

interface KeywordWeightValueProps {
  weight: number;
  /** Number only, for tight inline contexts such as chip editing. */
  compact?: boolean;
}

/** Closed-select rendering: number plus short label on one line. */
export const KeywordWeightValue: React.FC<KeywordWeightValueProps> = ({
  weight,
  compact = false,
}) => {
  const label = getKeywordWeightLabel(weight);
  if (compact) return <WeightNumber weight={weight} />;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      <WeightNumber weight={weight} />
      {label && (
        <Box
          component="span"
          sx={{ color: 'text.secondary', fontWeight: 400, fontSize: 13 }}
        >
          {label.short}
        </Box>
      )}
    </Box>
  );
};

interface KeywordWeightTooltipProps {
  weight: number;
  children: React.ReactElement;
}

/** Wraps a chip's weight number with a tooltip explaining the tier. */
export const KeywordWeightTooltip: React.FC<KeywordWeightTooltipProps> = ({
  weight,
  children,
}) => {
  const label = getKeywordWeightLabel(weight);
  if (!label) return children;
  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {weight} · {label.full}
          </Typography>
          <Typography variant="body2">{label.description}</Typography>
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};

/** Legend listing all six tiers, top tier first. Used in the info tooltip. */
export const KeywordWeightLegend: React.FC = () => (
  <Box sx={{ p: 1, maxWidth: 440 }} data-testid="keywords-weight-legend">
    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
      What each weight means
    </Typography>
    <Box
      component="ul"
      sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.75 }}
    >
      {KEYWORD_WEIGHT_LABELS.map(l => (
        <Box
          component="li"
          key={l.weight}
          sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}
          data-testid={`keywords-weight-legend-${l.weight}`}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              minWidth: 34,
              flexShrink: 0,
            }}
          >
            {l.weight}
          </Box>
          <Box component="span">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {l.full}.
            </Box>{' '}
            <Box component="span" sx={{ opacity: 0.85 }}>
              {l.description}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
    <Typography
      variant="caption"
      sx={{ display: 'block', mt: 1, opacity: 0.8 }}
    >
      Avoid high weights for generic terms.
    </Typography>
  </Box>
);
